import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ArrowLeft,
  BookOpen,
  FileText,
  Video,
  Download,
  ExternalLink,
  Shield,
  Users,
  GraduationCap,
  Image as ImageIcon,
  CheckCircle,
  Clock
} from 'lucide-react';

interface Resource {
  id: string;
  title: string;
  description: string;
  type: 'document' | 'video' | 'policy' | 'training';
  category: string;
  url?: string;
  icon: React.ReactNode;
}

interface Document {
  id: string;
  title: string;
  description: string;
  category: 'onboarding' | 'resources' | 'policies';
  file_url: string;
  file_name: string;
  file_size: number;
  posted_by_type: 'admin' | 'client';
  posted_by_name: string;
  created_at: string;
}

export default function Resources() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { theme, setTheme, setThemeTransient } = useTheme();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  useEffect(() => {
    setThemeTransient('light');
  }, [setThemeTransient]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [onboardingData, setOnboardingData] = useState<any>(null);
  const [downloadedDocs, setDownloadedDocs] = useState<Set<string>>(new Set());
  const [onboardingProgress, setOnboardingProgress] = useState(0);

  useEffect(() => {
    fetchLogo();
    fetchDocuments();
    fetchOnboardingData();
    fetchDownloadedDocs();
  }, [user?.id]);

  const fetchLogo = async () => {
    try {
      setIsLoadingLogo(true);
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'logo_url')
        .maybeSingle();

      if (!error && data?.value) {
        const img = new Image();
        img.onload = () => {
          setLogoUrl(data.value);
          setIsLoadingLogo(false);
        };
        img.onerror = () => setIsLoadingLogo(false);
        img.src = data.value;
      } else {
        setIsLoadingLogo(false);
      }
    } catch (err) {
      setIsLoadingLogo(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoadingDocuments(true);
      const { data, error } = await supabase
        .from('consultant_documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const fetchOnboardingData = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('consultant_onboarding')
        .select('*')
        .eq('consultant_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      setOnboardingData(data);
      calculateProgress(data);
    } catch (error) {
      console.error('Error fetching onboarding data:', error);
    }
  };

  const fetchDownloadedDocs = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('consultant_document_downloads')
        .select('document_type')
        .eq('consultant_id', user.id);

      if (error) throw error;
      const downloaded = new Set(data?.map(d => d.document_type) || []);
      setDownloadedDocs(downloaded);
    } catch (error) {
      console.error('Error fetching downloaded docs:', error);
    }
  };

  useEffect(() => {
    calculateProgress();
  }, [downloadedDocs, onboardingData]);

  const calculateProgress = () => {
    let completedItems = 0;
    const totalItems = 4;

    if (downloadedDocs.has('onboarding_guide')) completedItems++;
    if (downloadedDocs.has('timesheet_guidelines')) completedItems++;
    if (onboardingData?.references_status === 'verified') completedItems++;
    if (onboardingData?.drug_screening_status === 'completed') completedItems++;

    setOnboardingProgress(Math.round((completedItems / totalItems) * 100));
  };

  const trackDownload = async (documentType: string) => {
    if (!user?.id) return;

    try {
      await supabase
        .from('consultant_document_downloads')
        .upsert({
          consultant_id: user.id,
          document_type: documentType,
          downloaded_at: new Date().toISOString()
        }, {
          onConflict: 'consultant_id,document_type'
        });

      setDownloadedDocs(prev => new Set([...prev, documentType]));
      await fetchOnboardingData();
    } catch (error) {
      console.error('Error tracking download:', error);
    }
  };

  const hasStartedOnboarding = () => {
    return onboardingData !== null;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const resources: Resource[] = [
    {
      id: '1',
      title: 'Consultant Onboarding Guide',
      description: 'Complete guide for new consultants joining the Perplexity team',
      type: 'document',
      category: 'onboarding',
      icon: <GraduationCap className="w-6 h-6" />
    },
    {
      id: '2',
      title: 'Workday HCM Best Practices',
      description: 'Industry best practices for Workday HCM implementations',
      type: 'document',
      category: 'training',
      icon: <BookOpen className="w-6 h-6" />
    },
    {
      id: '3',
      title: 'Security & Compliance Policy',
      description: 'Information security policies and compliance requirements',
      type: 'policy',
      category: 'policies',
      icon: <Shield className="w-6 h-6" />
    },
    {
      id: '4',
      title: 'Code of Conduct',
      description: 'Professional standards and ethical guidelines for consultants',
      type: 'policy',
      category: 'policies',
      icon: <Users className="w-6 h-6" />
    },
    {
      id: '5',
      title: 'Workday Financials Training',
      description: 'Video series on Workday Financials module configuration',
      type: 'video',
      category: 'training',
      icon: <Video className="w-6 h-6" />
    },
    {
      id: '6',
      title: 'Timesheet Submission Guidelines',
      description: 'How to properly log hours and submit timesheets',
      type: 'document',
      category: 'onboarding',
      icon: <Clock className="w-6 h-6" />
    },
    {
      id: '7',
      title: 'Client Communication Standards',
      description: 'Best practices for professional client communication',
      type: 'policy',
      category: 'policies',
      icon: <FileText className="w-6 h-6" />
    },
    {
      id: '8',
      title: 'Workday Recruiting Module',
      description: 'Comprehensive training on Workday Recruiting features',
      type: 'video',
      category: 'training',
      icon: <Video className="w-6 h-6" />
    },
    {
      id: '9',
      title: 'Project Templates',
      description: 'Downloadable templates for common project deliverables',
      type: 'document',
      category: 'training',
      icon: <Download className="w-6 h-6" />
    },
    {
      id: '10',
      title: 'Benefits & Compensation',
      description: 'Overview of consultant benefits and compensation structure',
      type: 'policy',
      category: 'policies',
      icon: <FileText className="w-6 h-6" />
    }
  ];

  const categories = [
    { id: 'all', name: 'All Resources', icon: <BookOpen className="w-5 h-5" /> },
    { id: 'onboarding', name: 'Onboarding', icon: <GraduationCap className="w-5 h-5" /> },
    { id: 'training', name: 'Training', icon: <Video className="w-5 h-5" /> },
    { id: 'policies', name: 'Policies', icon: <Shield className="w-5 h-5" /> }
  ];

  const filteredResources = activeCategory === 'all'
    ? resources
    : resources.filter(r => r.category === activeCategory);

  const getResourceTypeBadge = (type: string) => {
    const badges = {
      document: 'bg-blue-100 text-blue-800',
      video: 'bg-purple-100 text-purple-800',
      policy: 'bg-red-100 text-red-800',
      training: 'bg-green-100 text-green-800'
    };
    return badges[type as keyof typeof badges] || badges.document;
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Header */}
      <nav className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="text-white -mt-4 -mb-4 cursor-default select-none">
              {isLoadingLogo ? (
                <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : logoUrl ? (
                <img
                  src="/1.png"
                  alt="Perplexity"
                  className="h-40 w-auto object-contain"
                />
              ) : (
                <ImageIcon size={32} className="text-primary" />
              )}
            </div>
            <button
              onClick={() => navigate('/consultant/dashboard')}
              className="text-white hover:text-primary transition-colors flex items-center"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">Onboarding, Resources & Policies</h1>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Access training materials, documentation, and company policies
            </p>
          </div>

          {/* Categories */}
          <div className="mb-8 flex flex-wrap gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                  activeCategory === category.id
                    ? 'bg-primary text-black'
                    : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                {category.icon}
                <span className="ml-2">{category.name}</span>
              </button>
            ))}
          </div>

          {/* Onboarding Progress (only show in onboarding category) */}
          {activeCategory === 'onboarding' && (
            <div className={`mb-8 p-6 rounded-xl ${
              theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
            }`}>
              <h2 className="text-2xl font-bold mb-4">Your Onboarding Progress</h2>

              {!hasStartedOnboarding() ? (
                <div className={`p-6 rounded-lg text-center ${
                  theme === 'dark' ? 'bg-gray-700/50' : 'bg-blue-50 border border-blue-200'
                }`}>
                  <Clock className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                  <p className="text-lg font-semibold mb-2">Onboarding Not Started</p>
                  <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                    Your onboarding process will begin once our admin team initiates it. You'll be notified when you can start downloading documents and completing onboarding steps.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {downloadedDocs.has('onboarding_guide') ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-gray-300 mr-3"></div>
                        )}
                        <span>Download Consultant Onboarding Guide</span>
                      </div>
                      <span className={downloadedDocs.has('onboarding_guide') ? 'text-green-500 font-semibold' : `font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {downloadedDocs.has('onboarding_guide') ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {downloadedDocs.has('timesheet_guidelines') ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-gray-300 mr-3"></div>
                        )}
                        <span>Download Timesheet Submission Guidelines</span>
                      </div>
                      <span className={downloadedDocs.has('timesheet_guidelines') ? 'text-green-500 font-semibold' : `font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {downloadedDocs.has('timesheet_guidelines') ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {onboardingData?.references_status === 'verified' ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-gray-300 mr-3"></div>
                        )}
                        <span>References Verification</span>
                      </div>
                      <span className={onboardingData?.references_status === 'verified' ? 'text-green-500 font-semibold' : `font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {onboardingData?.references_status === 'verified' ? 'Verified' : onboardingData?.references_status === 'rejected' ? 'Rejected' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {onboardingData?.background_check_status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-gray-300 mr-3"></div>
                        )}
                        <span>Background Check</span>
                      </div>
                      <span className={onboardingData?.background_check_status === 'completed' ? 'text-green-500 font-semibold' : `font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {onboardingData?.background_check_status === 'completed' ? 'Completed' : onboardingData?.background_check_status === 'failed' ? 'Failed' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        {onboardingData?.drug_screening_status === 'completed' ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                        ) : (
                          <div className="w-5 h-5 rounded-full border-2 border-gray-300 mr-3"></div>
                        )}
                        <span>Drug Screening</span>
                      </div>
                      <span className={onboardingData?.drug_screening_status === 'completed' ? 'text-green-500 font-semibold' : `font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {onboardingData?.drug_screening_status === 'completed' ? 'Completed' : onboardingData?.drug_screening_status === 'failed' ? 'Failed' : 'Pending'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">Overall Progress</span>
                      <span className="font-semibold">{onboardingProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full transition-all duration-500" style={{ width: `${onboardingProgress}%` }}></div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Uploaded Documents Section */}
          {!loadingDocuments && documents.length > 0 && (
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Client & Admin Documents</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {(['onboarding', 'resources', 'policies'] as const).map((category) => {
                  const categoryDocs = documents.filter(doc => doc.category === category);
                  if (categoryDocs.length === 0) return null;

                  return (
                    <div key={category} className={`p-6 rounded-xl ${
                      theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
                    }`}>
                      <h3 className="text-xl font-bold mb-4 capitalize">{category}</h3>
                      <div className="space-y-3">
                        {categoryDocs.map((doc) => (
                          <div
                            key={doc.id}
                            className={`p-4 rounded-lg border ${
                              theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-start space-x-3 mb-2">
                              <FileText className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold break-words">{doc.title}</h4>
                                {doc.description && (
                                  <p className={`text-sm mt-1 break-words ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    {doc.description}
                                  </p>
                                )}
                                <div className="flex items-center space-x-2 mt-2 text-xs">
                                  <span className={`px-2 py-1 rounded ${
                                    doc.posted_by_type === 'admin'
                                      ? 'bg-blue-500/20 text-blue-600 font-semibold'
                                      : 'bg-green-500/20 text-green-600 font-semibold'
                                  }`}>
                                    {doc.posted_by_type === 'admin' ? 'Perplexity Admin' : doc.posted_by_name}
                                  </span>
                                  <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}>
                                    {formatFileSize(doc.file_size)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <a
                              href={doc.file_url}
                              download
                              className="w-full mt-3 bg-primary text-black font-semibold py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Resources Grid */}
          <div>
            <h2 className="text-3xl font-bold mb-6">Additional Resources</h2>

            {!hasStartedOnboarding() ? (
              <div className={`p-8 rounded-xl text-center ${
                theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-blue-50 border border-blue-200'
              }`}>
                <Clock className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-3">Resources Available After Onboarding Starts</h3>
                <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  Once your onboarding starts, download links and resources will be made available here. Our admin team will notify you when you can begin accessing these materials.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResources.map((resource) => {
                  const isOnboardingDoc = resource.title === 'Consultant Onboarding Guide' || resource.title === 'Timesheet Submission Guidelines';
                  const documentType = resource.title === 'Consultant Onboarding Guide' ? 'onboarding_guide' : resource.title === 'Timesheet Submission Guidelines' ? 'timesheet_guidelines' : null;
                  const isDownloaded = documentType ? downloadedDocs.has(documentType) : false;

                  return (
                    <div
                      key={resource.id}
                      className={`p-6 rounded-xl transition-all duration-200 ${
                        isOnboardingDoc ? 'hover:scale-105' : ''
                      } ${
                        theme === 'dark'
                          ? 'bg-gray-800 border border-gray-700 hover:bg-gray-750'
                          : 'bg-white border border-gray-200 shadow-sm hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className={`p-3 rounded-lg ${
                          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          <div className="text-primary">{resource.icon}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getResourceTypeBadge(resource.type)}`}>
                            {resource.type.charAt(0).toUpperCase() + resource.type.slice(1)}
                          </span>
                          {isDownloaded && (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                              Downloaded
                            </span>
                          )}
                        </div>
                      </div>

                      <h3 className="text-xl font-bold mb-2">{resource.title}</h3>
                      <p className={`mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        {resource.description}
                      </p>

                      <button
                        onClick={() => {
                          if (isOnboardingDoc && documentType) {
                            trackDownload(documentType);
                          }
                        }}
                        className="w-full bg-primary text-black font-semibold py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center"
                      >
                        {resource.type === 'video' ? (
                          <>
                            <Video className="w-4 h-4 mr-2" />
                            Watch Now
                          </>
                        ) : resource.url ? (
                          <>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open Resource
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className={`mt-12 p-6 rounded-xl text-center ${
            theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            <BookOpen className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Need Additional Help?</h2>
            <p className={`mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              Can't find what you're looking for? Reach out to our support team.
            </p>
            <button className="bg-primary text-black font-semibold px-8 py-3 rounded-lg hover:bg-primary/90 transition-colors">
              Contact Support
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
